import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@altimateai/lego";
import dayjs from "dayjs";
import { BackButton } from "@/components/button";
import { DateRange } from "@/components/dateRange";
import { RelativeComponentLoader } from "@/components/loader";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useAppState } from "@/modules/app";
import { useClusterDetail } from "./hooks";
import { getClusterSourceLabel } from "./helpers";
import { ClusterCostChart, ClusterUtilizationChart, ClusterOpportunities } from "./components";
import ClusterAutoTuneControl from "./auto-tune/ClusterAutoTuneControl";
import ClusterAutoTunePerformancePanel from "./auto-tune/ClusterAutoTunePerformancePanel";
import { useBreadcrumb } from "@/components/breadcrumbs";

const AGGREGATION_OPTIONS = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

const ClusterDetail = () => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const { isDatabricksTenant, disableAllPurposeAutotune } = useAppState();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const [aggregation, setAggregation] = useState<"day" | "week" | "month">("day");
  const [activeTab, setActiveTab] = useState<string>("overview");

  const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
  const endDateStr = dayjs(endDate).format("YYYY-MM-DD");

  const {
    data: clusterData,
    isLoading: isClusterLoading,
    error: clusterError,
  } = useClusterDetail({ cluster_id: clusterId || "" }, Boolean(clusterId));

  useBreadcrumb(clusterData?.cluster_name ?? clusterId ?? "Cluster", {
    defaultParents: [{ label: "Clusters", pathname: "/clusters" }],
  });

  const instanceId = clusterData?.instance_id ?? undefined;

  if (!clusterId) {
    return (
      <Stack className="al-p-4">
        <Typography className="al-text-red-500">Cluster ID is required</Typography>
      </Stack>
    );
  }

  if (isClusterLoading) {
    return (
      <div className="al-h-full al-content-center">
        <RelativeComponentLoader label="Loading cluster details..." />
      </div>
    );
  }

  if (clusterError || !clusterData) {
    return (
      <Stack direction="column" gap="lg" className="al-p-4">
        <Stack direction="row" align="center" gap="md">
          <BackButton defaultUrl="/clusters" />
          <Typography variant="h4">Cluster Details</Typography>
        </Stack>
        <Typography className="al-text-red-500">Failed to load cluster details</Typography>
      </Stack>
    );
  }

  const renderConfigItem = (label: string, value: string) => (
    <Stack direction="column" gap="xs" key={label}>
      <Typography size="sm" className="al-text-gray-500">
        {label}
      </Typography>
      <Typography weight="medium">{value}</Typography>
    </Stack>
  );

  // Shared by the Costs + Utilization tabs (identical control).
  const aggregationSelector = (
    <Stack direction="row" align="center" gap="sm">
      <Typography size="sm" className="al-text-gray-600">
        Aggregate:
      </Typography>
      <Select
        value={aggregation}
        onValueChange={v => setAggregation(v as "day" | "week" | "month")}
      >
        <SelectTrigger className="al-w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AGGREGATION_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Stack>
  );

  // Auto Tune is a Databricks-only feature available only on all-purpose
  // clusters, and the control needs the tenant instance_id to scope its
  // reads/writes. The ``disableAllPurposeAutotune`` tenant flag is a
  // kill-switch: when set, the tab + header control are hidden and the
  // control never mounts, so no status/preflight call is made.
  const showAutoTune =
    isDatabricksTenant &&
    !disableAllPurposeAutotune &&
    clusterData.compute_type === "all_purpose" &&
    instanceId != null;

  return (
    <Stack direction="column" gap="lg">
      <Stack direction="row" align="center" justify="between">
        <Stack direction="row" align="center" gap="md">
          <BackButton defaultUrl="/clusters" />
          <Typography variant="h4">{clusterData.cluster_name}</Typography>
        </Stack>
        {showAutoTune ? (
          <ClusterAutoTuneControl
            clusterId={clusterId}
            clusterName={clusterData.cluster_name}
            instanceId={instanceId!}
            workspaceId={clusterData.workspace_id ?? undefined}
            workerNodeType={clusterData.worker_node_type}
            driverNodeType={clusterData.driver_node_type}
            minAutoscaleWorkers={clusterData.min_autoscale_workers}
            maxAutoscaleWorkers={clusterData.max_autoscale_workers}
            workerCount={clusterData.worker_count}
          />
        ) : null}
      </Stack>

      {/* Configuration Section */}
      <Card className="al-p-4">
        <Typography size="lg" weight="medium" className="al-mb-4">
          Configuration
        </Typography>
        <div className="al-grid al-grid-cols-2 al-gap-4 md:al-grid-cols-4">
          {renderConfigItem("Workspace", clusterData.workspace_name || clusterData.workspace_id)}
          {renderConfigItem("Driver Node", clusterData.driver_node_type || "-")}
          {renderConfigItem("Worker Node", clusterData.worker_node_type || "-")}
          {renderConfigItem(
            "Workers",
            clusterData.min_autoscale_workers !== null && clusterData.max_autoscale_workers !== null
              ? `${clusterData.min_autoscale_workers} - ${clusterData.max_autoscale_workers} (autoscale)`
              : String(clusterData.worker_count ?? "-")
          )}
          {renderConfigItem("Cluster Source", getClusterSourceLabel(clusterData.cluster_source))}
          {renderConfigItem("Spark Version", clusterData.spark_version || "-")}
          {renderConfigItem("Runtime Engine", clusterData.runtime_engine || "-")}
          {renderConfigItem(
            "Auto Termination",
            clusterData.autotermination_minutes
              ? `${clusterData.autotermination_minutes} minutes`
              : "Disabled"
          )}
        </div>
      </Card>

      {/* Cluster Details */}
      <Card className="al-p-4">
        <Stack direction="row" align="center" justify="between" className="al-mb-4">
          <Typography size="lg" weight="medium">
            Cluster Details
          </Typography>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            onDateRangeSelect={onUpdate}
            id="cluster-detail-date-range"
            maxDate={new Date()}
          />
        </Stack>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="al-w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
            {showAutoTune ? <TabsTrigger value="autotune">Auto Tune</TabsTrigger> : null}
          </TabsList>
          <TabsContent value="overview" className="al-mt-4">
            <Tabs defaultValue="costs" className="al-w-full">
              <TabsList className="al-grid al-w-full al-grid-cols-2">
                <TabsTrigger value="costs">Costs</TabsTrigger>
                <TabsTrigger value="utilization">Utilization</TabsTrigger>
              </TabsList>
              <TabsContent value="costs" className="al-mt-4">
                <Stack direction="row" align="center" justify="between" className="al-mb-4">
                  <Typography weight="medium">Costs</Typography>
                  {aggregationSelector}
                </Stack>
                <ClusterCostChart
                  clusterId={clusterId}
                  startDate={startDateStr}
                  endDate={endDateStr}
                  aggregation={aggregation}
                />
              </TabsContent>
              <TabsContent value="utilization" className="al-mt-4">
                <Stack direction="row" align="center" justify="between" className="al-mb-4">
                  <Typography weight="medium">Utilization</Typography>
                  {aggregationSelector}
                </Stack>
                <ClusterUtilizationChart
                  clusterId={clusterId}
                  startDate={startDateStr}
                  endDate={endDateStr}
                  aggregation={aggregation}
                />
              </TabsContent>
            </Tabs>
          </TabsContent>
          <TabsContent value="opportunities" className="al-mt-4">
            <ClusterOpportunities
              clusterId={clusterId}
              clusterName={clusterData.cluster_name}
              instanceId={instanceId != null ? Number(instanceId) : undefined}
              workspaceName={clusterData.workspace_name ?? undefined}
              workspaceId={clusterData.workspace_id ?? undefined}
            />
          </TabsContent>
          {showAutoTune ? (
            <TabsContent value="autotune" className="al-mt-4">
              <ClusterAutoTunePerformancePanel
                clusterId={clusterId}
                instanceId={instanceId}
                workspaceId={clusterData.workspace_id ?? undefined}
                startDate={startDateStr}
                endDate={endDateStr}
              />
            </TabsContent>
          ) : null}
        </Tabs>
      </Card>
    </Stack>
  );
};

export default ClusterDetail;
